import type { CalibrationTrial } from '../types';

/**
 * The Zielübung protocol (§5.6).
 *
 * A functional access assessment, not a medical one — the same thing an occupational therapist
 * would do with a laminated grid, and exactly the input this design needs. We cannot get the
 * clinical assessment, so the app performs the part that decides the settings.
 */

/** 3 × 3, row-major. Position 0 is top-left, 8 is bottom-right. */
export const POSITIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8] as const;

/** Largest first, so the exercise starts with an easy success rather than a failure. */
export const TARGET_SIZES_MM = [34, 28, 24, 20, 18] as const;

export const TOTAL_TRIALS = POSITIONS.length * TARGET_SIZES_MM.length;

/**
 * Below this many completed trials the proposal is marked provisional.
 *
 * Fatigue is the most likely reason a run ends early — capability at 18:00 is far below capability
 * at 10:00 — so stopping halfway has to be a supported outcome that still yields something, rather
 * than a lost session. But the caregiver must be able to see that it rests on less evidence.
 */
export const PROVISIONAL_BELOW = 27;

/** How long a target waits before it counts as never touched. */
export const TRIAL_TIMEOUT_MS = 8000;

export interface PlannedTrial {
  position: number;
  targetMm: number;
}

/**
 * One pass: every position at every size, exactly once, in a shuffled order.
 *
 * Shuffled because a predictable sweep would let the person anticipate rather than search, and
 * searching is the thing being measured. The shuffle is injected so tests are deterministic.
 */
export function planTrials(random: () => number): PlannedTrial[] {
  const trials: PlannedTrial[] = [];
  for (const targetMm of TARGET_SIZES_MM) {
    for (const position of POSITIONS) trials.push({ position, targetMm });
  }

  // Fisher-Yates over a copy.
  for (let i = trials.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const a = trials[i]!;
    const b = trials[j]!;
    trials[i] = b;
    trials[j] = a;
  }
  return trials;
}

/** Column of a position, 0 = left. The field-loss rule reads these. */
export const columnOf = (position: number): 0 | 1 | 2 => (position % 3) as 0 | 1 | 2;

/** Row of a position, 0 = top. */
export const rowOf = (position: number): 0 | 1 | 2 => Math.floor(position / 3) as 0 | 1 | 2;

export const isMiss = (trial: CalibrationTrial): boolean => !trial.hit;
